import { useSocketClient } from '../../../../../providers/SocketContext';
import { useCurrentUser } from '../../../../../providers/UserProvider';
import { useCheckDocumentLockStatus } from 'components/editor/hooks/useCheckDocumentLockStatus';
import { useAppDispatch, useAppSelector } from '../../../grid/reduxStore/Store';
import type {
  AllTextDefaultStylesType,
  AllTextStyleState,
  TextDefaultSelectorsEnum,
  TextDefaultStylesState,
  TableTextDefaultStylesState,
  UpdateTextDefaultStylingPayloadType,
} from '../../../grid/reduxStore/textDefaultStylingSlice';
import {
  selectAllTextDefaultStyles,
  selectTextDefaultStyles,
  selectTableTextDefaultStyles,
  setTextDefaultStyling,
  setTableTextDefaultStyling,
  TableDefaultClassNamesEnum,
  TextDefaultStyleTypesIdentifier,
} from '../../../grid/reduxStore/textDefaultStylingSlice';
import type { TableRowStyleState, UpdateTableRowStylingPayloadType } from '../../../grid/reduxStore/tableRowDefaultStyleSlice';
import { selectTableRowStyles, setTableRowStyle } from '../../../grid/reduxStore/tableRowDefaultStyleSlice';
import { AcknowledgmentResponseStatus, DocumentSettingsEvents } from '../../../../../services/socket/SocketEvents';
import type { DocumentSettingsType, SetDocumentSettingsType } from './DocumentSettingsContext';
import { DEFAULT_PAGE_SIZE, getPageSizeForOrientation, PAGE_SIZES, type PageOrientation } from '../../../shared/gridConfig';
import { useCallback, useEffect, useState } from 'react';
import type { Currency } from '../../../grid/reduxStore/documentSettingsSlice';
import {
  defaultCurrencySettings,
  selectActiveCurrencySettings,
  setDocumentBackgroundColor,
  setInitialCurrencySettings,
} from '../../../grid/reduxStore/documentSettingsSlice';
import { UndoRedoManagerCommandVersion } from '../../../UndoRedo/UndoRedoManagerCommandVersion';
import type { DocumentSettingsChangePayload, MovedBlock } from '../../../UndoRedo/UpdateDocumentSettingsCommand.ts';
import { UpdateDocumentSettingsCommand } from '../../../UndoRedo/UpdateDocumentSettingsCommand.ts';
import { DEFAULT_DOCUMENT_BACKGROUND_COLOR } from '../../../shared/documentColorDefaults';

const useNewDocumentSettings = () => {
  const defaultMarginValue = 0;
  const { socketClient, isConnected } = useSocketClient();
  const { data: currentUser } = useCurrentUser();
  const checkDocumentLockStatus = useCheckDocumentLockStatus();
  const dispatch = useAppDispatch();

  const allTextDefaultStylesRedux = useAppSelector(selectAllTextDefaultStyles);
  const textDefaultStylesRedux = useAppSelector(selectTextDefaultStyles);
  const tableTextDefaultStylesRedux = useAppSelector(selectTableTextDefaultStyles);
  const tableRowStylesRedux = useAppSelector(selectTableRowStyles);
  const currencySettingsRedux = useAppSelector(selectActiveCurrencySettings) || defaultCurrencySettings;

  const [isLoaded, setIsLoaded] = useState(false);

  const [documentSettings, updateDocumentSettings] = useState<DocumentSettingsType>({
    margin: {
      top: defaultMarginValue,
      bottom: defaultMarginValue,
      left: defaultMarginValue,
      right: defaultMarginValue,
    },
    backgroundColor: DEFAULT_DOCUMENT_BACKGROUND_COLOR,
    pageOrientation: 'portrait',
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const commandDeps = { dispatch, socketClient, checkDocumentLockStatus };

  const onDocumentSettingsLoadCallback = useCallback(
    ({
      content: newSettings,
      status: responseStatus,
    }: {
      content: DocumentSettingsType & {
        textDefaultStyles?: AllTextStyleState;
      } & {
        tableDefaultStyles?: TableRowStyleState;
      };
      status: AcknowledgmentResponseStatus;
    }) => {
      if (responseStatus === AcknowledgmentResponseStatus.FAILURE) {
        return false;
      }

      const resolvedOrientation: PageOrientation = newSettings.pageOrientation || 'portrait';
      // Backend already orients a stored pageSize; only fall back (FE state only, not saved) when missing or unknown.
      const loadedPageSize = newSettings.pageSize;
      const resolvedPageSize =
        loadedPageSize && PAGE_SIZES.some((size) => size.label === loadedPageSize.label)
          ? { ...loadedPageSize }
          : getPageSizeForOrientation(undefined, resolvedOrientation);

      updateDocumentSettings({
        margin: { ...newSettings.margin },
        backgroundColor: newSettings.backgroundColor,
        pageOrientation: resolvedOrientation,
        pageSize: resolvedPageSize,
      });
      dispatch(setDocumentBackgroundColor(newSettings.backgroundColor));

      if (newSettings.textDefaultStyles) {
        const textStyles: Partial<TextDefaultStylesState> = {};
        const tableStyles: Partial<TableTextDefaultStylesState> = {};

        Object.entries(newSettings.textDefaultStyles).forEach(([key, value]) => {
          if (Object.values(TableDefaultClassNamesEnum).includes(key as TableDefaultClassNamesEnum)) {
            tableStyles[key as TableDefaultClassNamesEnum] = value as AllTextDefaultStylesType;
          } else {
            textStyles[key as TextDefaultSelectorsEnum] = value as AllTextDefaultStylesType;
          }
        });

        dispatch(setTextDefaultStyling(textStyles as TextDefaultStylesState));
        dispatch(setTableTextDefaultStyling(tableStyles as TableTextDefaultStylesState));
      }

      if (newSettings.tableDefaultStyles) {
        dispatch(setTableRowStyle({ ...tableRowStylesRedux, ...newSettings.tableDefaultStyles } as TableRowStyleState));
      }

      if (newSettings.currencySettings) {
        const currencySettings: Currency = {
          code: newSettings.currencySettings['currencyCode'] || defaultCurrencySettings.code,
          symbol: newSettings.currencySettings['currencySign'] || defaultCurrencySettings.symbol,
        };
        dispatch(setInitialCurrencySettings(currencySettings));
      }

      setIsLoaded(true);
    },
    [dispatch, tableRowStylesRedux]
  );

  useEffect(() => {
    if (!isConnected) return;

    socketClient.publish(DocumentSettingsEvents.DOCUMENT_SETTINGS_LOAD, {}, onDocumentSettingsLoadCallback);
  }, [isConnected]);

  const setDocumentSettings: SetDocumentSettingsType = useCallback(
    async (newDocumentSettings: DocumentSettingsType, previousState?: DocumentSettingsType, movedBlocks?: MovedBlock[]) => {
      if (!isLoaded) return;
      const previousDocumentSettings = previousState || documentSettings;

      const currentFullSettings: DocumentSettingsType = {
        ...previousDocumentSettings,
        currencySettings: currencySettingsRedux,
      };

      const command = new UpdateDocumentSettingsCommand(commandDeps, {
        previousState: { documentSettings: structuredClone(currentFullSettings) },
        newState: { documentSettings: structuredClone(newDocumentSettings) },
        updateLocalState: updateDocumentSettings,
        movedBlocks,
        userId: currentUser.id,
      });

      const success = await command.execute();

      if (success) {
        dispatch(setDocumentBackgroundColor(newDocumentSettings.backgroundColor));
        const undoRedoManager = UndoRedoManagerCommandVersion.getUndoRedoManager();
        undoRedoManager.pushUndoRedoCommands(command);
      }
    },
    [dispatch, socketClient, checkDocumentLockStatus, documentSettings, currencySettingsRedux, isLoaded, currentUser.id]
  );

  const updateTextDefaultStyle = useCallback(
    async ({ type, style, TextDefaultStyleTypes = TextDefaultStyleTypesIdentifier.DOCUMENT }: UpdateTextDefaultStylingPayloadType) => {
      if (!isLoaded) return;

      const currentAllStyles = structuredClone(allTextDefaultStylesRedux);
      const newAllStyles = { ...currentAllStyles, [type]: { ...currentAllStyles[type], ...style } };

      const currentTextStyles = structuredClone(textDefaultStylesRedux);
      const newTextStyles = { ...currentTextStyles, [type]: { ...currentTextStyles[type], ...style } };

      const currentTableTextStyles = structuredClone(tableTextDefaultStylesRedux);
      const newTableTextStyles = { ...currentTableTextStyles, [type]: { ...currentTableTextStyles[type], ...style } };

      const previousState: DocumentSettingsChangePayload = {
        allTextDefaultStyles: currentAllStyles,
        ...(TextDefaultStyleTypes === TextDefaultStyleTypesIdentifier.DOCUMENT && {
          textDefaultStyles: currentTextStyles,
        }),
        ...(TextDefaultStyleTypes === TextDefaultStyleTypesIdentifier.TABLE && {
          tableTextDefaultStyles: currentTableTextStyles,
        }),
      };

      const newState: DocumentSettingsChangePayload = {
        allTextDefaultStyles: newAllStyles,
        ...(TextDefaultStyleTypes === TextDefaultStyleTypesIdentifier.DOCUMENT && {
          textDefaultStyles: newTextStyles,
        }),
        ...(TextDefaultStyleTypes === TextDefaultStyleTypesIdentifier.TABLE && {
          tableTextDefaultStyles: newTableTextStyles,
        }),
      };

      const command = new UpdateDocumentSettingsCommand(commandDeps, {
        previousState,
        newState,
      });

      const success = await command.execute();

      if (success) {
        const undoRedoManager = UndoRedoManagerCommandVersion.getUndoRedoManager();
        undoRedoManager.pushUndoRedoCommands(command);
      }
    },
    [
      dispatch,
      socketClient,
      checkDocumentLockStatus,
      allTextDefaultStylesRedux,
      textDefaultStylesRedux,
      tableTextDefaultStylesRedux,
      isLoaded,
    ]
  );

  const updateTableRowStyle = useCallback(
    async ({ type, style }: UpdateTableRowStylingPayloadType) => {
      if (!isLoaded) return;

      const currentStyles = structuredClone(tableRowStylesRedux);
      const newStyles = { ...currentStyles, [type]: { ...currentStyles[type], ...style } };

      const command = new UpdateDocumentSettingsCommand(commandDeps, {
        previousState: { tableRowStyles: currentStyles },
        newState: { tableRowStyles: newStyles },
      });

      const success = await command.execute();

      if (success) {
        const undoRedoManager = UndoRedoManagerCommandVersion.getUndoRedoManager();
        undoRedoManager.pushUndoRedoCommands(command);
      }
    },
    [dispatch, socketClient, checkDocumentLockStatus, tableRowStylesRedux, isLoaded]
  );

  const previewDocumentSettings = useCallback((newDocumentSettings: Partial<DocumentSettingsType>) => {
    updateDocumentSettings((prev) => ({ ...prev, ...newDocumentSettings }));
  }, []);

  return { documentSettings, previewDocumentSettings, setDocumentSettings, updateTextDefaultStyle, updateTableRowStyle };
};

export default useNewDocumentSettings;
